import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, Kanban, BarChart3, Settings, LogOut, UserCog, Package, UserCircle, Hand } from "lucide-react";
import api from "../lib/api";
import { useAuth, useCurrency } from "../lib/contexts";
import useActivityPing from "../lib/useActivityPing";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback } from "./ui/avatar";

const NavItem = ({ to, icon: Icon, children, testid }) => (
  <NavLink
    to={to}
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
        isActive
          ? "bg-primary/10 text-primary"
          : "text-neutral-700 hover:bg-neutral-100"
      }`
    }
  >
    <Icon className="w-4 h-4" />
    <span className="flex-1 flex items-center gap-2">{children}</span>
  </NavLink>
);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { currency, set } = useCurrency();
  const nav = useNavigate();

  // Tick the header clock every 30s so the time shown stays fresh without
  // being wasteful.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const firstName = (user?.name || "").trim().split(/\s+/)[0] || "";
  const hr = now.getHours();
  const greeting = hr < 12 ? "Good morning" : hr < 17 ? "Good afternoon" : "Good evening";
  const dateStr = now.toLocaleDateString(undefined, {
    weekday: "long", day: "numeric", month: "short", year: "numeric",
  });
  const timeStr = now.toLocaleTimeString(undefined, {
    hour: "numeric", minute: "2-digit",
  });

  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const isManager = user?.role === "manager";
  // Only sales staff activity is tracked.
  useActivityPing(user?.role === "staff");
  const loc = useLocation();
  const [openLeads, setOpenLeads] = useState(0);
  useEffect(() => {
    const refresh = () => api.get("/leads", { params: { status: "open" } })
      .then((r) => setOpenLeads((r.data || []).length)).catch(() => {});
    refresh();
    // Grabs and releases happen elsewhere in the app, so listen for them
    // instead of leaving a stale badge until the next navigation.
    window.addEventListener("leads-changed", refresh);
    const id = setInterval(refresh, 60000);
    return () => { window.removeEventListener("leads-changed", refresh); clearInterval(id); };
  }, [loc.pathname]);

  return (
    <div className="min-h-screen flex bg-background bg-grain">
      <aside className="w-60 shrink-0 border-r border-border bg-white/70 backdrop-blur-xl flex flex-col">
        <div className="px-5 py-5 border-b border-border">
          <div className="flex items-center gap-3">
            <img
              src="/logo.webp"
              alt="Sun Nutrafoods"
              data-testid="brand-logo"
              className="h-10 w-auto object-contain"
            />
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-l border-border pl-3">
              CRM
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <NavItem to="/dashboard" icon={LayoutDashboard} testid="nav-dashboard">Dashboard</NavItem>
          <NavItem to="/contacts" icon={Users} testid="nav-contacts">Contacts</NavItem>
          <NavItem to="/deals" icon={Kanban} testid="nav-deals">Deals Pipeline</NavItem>
          <NavItem to="/grab-a-deal" icon={Hand} testid="nav-grab">
            Grab A Deal
            {openLeads > 0 && (
              <span data-testid="nav-grab-count"
                className="ml-auto text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded-full">{openLeads}</span>
            )}
          </NavItem>
          <NavItem to="/reports" icon={BarChart3} testid="nav-reports">Reports</NavItem>
          <NavItem to="/settings/account" icon={UserCircle} testid="nav-account">Account Settings</NavItem>
          {isManager && <NavItem to="/settings/users" icon={UserCog} testid="nav-users">Manage Users</NavItem>}
          {isManager && <NavItem to="/settings/products" icon={Package} testid="nav-products">Manage Products</NavItem>}
          {isManager && <NavItem to="/settings/fields" icon={Settings} testid="nav-fields">Manage Fields</NavItem>}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="w-9 h-9">
              <AvatarFallback className="bg-primary text-white text-xs font-semibold">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{user?.role}</div>
            </div>
          </div>
          <Button
            data-testid="logout-btn"
            variant="ghost"
            size="sm"
            onClick={async () => { await logout(); nav("/login"); }}
            className="w-full justify-start mt-1 text-neutral-600 hover:text-destructive"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>
      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border bg-white/70 backdrop-blur-xl flex items-center justify-between px-6 gap-3 no-print">
          <div className="min-w-0" data-testid="header-welcome">
            <div className="text-sm font-semibold text-neutral-900 leading-tight truncate">
              {greeting}{user?.name ? `, ${user.name}` : firstName ? `, ${firstName}` : ""}
            </div>
            <div className="text-[11px] text-muted-foreground leading-tight mt-0.5" data-testid="header-datetime">
              {dateStr} · {timeStr}
            </div>
          </div>
          <div className="flex items-center bg-neutral-100 rounded-md p-0.5" data-testid="currency-toggle">
            {["INR", "USD"].map((c) => (
              <button
                key={c}
                data-testid={`currency-${c}`}
                onClick={() => set(c)}
                className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${
                  currency === c ? "bg-white text-primary shadow-sm" : "text-neutral-500 hover:text-neutral-800"
                }`}
              >
                {c === "INR" ? "₹ INR" : "$ USD"}
              </button>
            ))}
          </div>
        </header>
        <div className="flex-1 overflow-auto p-6">{children}</div>
      </main>
    </div>
  );
}
